import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  BrainCircuit, Coins, Link2, ArrowLeftRight, CreditCard,
  LineChart, Users, Wrench, Globe,
} from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";

const STATUS = {
  live: { label: "Live", dot: "bg-emerald-400", pill: "border-emerald-400/30 bg-emerald-400/10 text-emerald-400" },
  dev: { label: "In Development", dot: "bg-amber-400", pill: "border-amber-400/30 bg-amber-400/10 text-amber-400" },
  future: { label: "Future", dot: "bg-gray-500", pill: "border-gray-500/30 bg-gray-500/10 text-gray-400" },
};

const ITEMS = [
  { icon: BrainCircuit, name: "AI Trading", status: "live", desc: "AI-managed trading strategies across digital markets." },
  { icon: Coins, name: "WIN Token", status: "live", desc: "150M fixed-supply token at the core of the ecosystem." },
  { icon: Link2, name: "WIN Blockchain", status: "dev", desc: "Next-generation blockchain infrastructure." },
  { icon: ArrowLeftRight, name: "WIN Exchange", status: "dev", desc: "Dedicated digital asset exchange." },
  { icon: CreditCard, name: "Winter Pay", status: "future", desc: "Fast, low-cost digital payment infrastructure." },
  { icon: LineChart, name: "Market Insights", status: "future", desc: "Market intelligence, trends and signals." },
  { icon: Users, name: "Trader Marketplace", status: "future", desc: "A marketplace built around traders." },
  { icon: Wrench, name: "Advanced AI Financial Tools", status: "future", desc: "Next wave of intelligent financial tooling." },
  { icon: Globe, name: "Global Expansion", status: "future", desc: "Broader reach across markets and regions." },
];

const FILTERS = [
  { key: "all", label: "All" },
  { key: "live", label: "Live" },
  { key: "dev", label: "In Development" },
  { key: "future", label: "Future" },
];

export default function Ecosystem() {
  const [filter, setFilter] = useState("all");
  const visible = ITEMS.filter((i) => filter === "all" || i.status === filter);

  return (
    <section id="ecosystem" className="relative py-24 sm:py-32 border-t border-white/[0.06]" data-testid="ecosystem-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          chapter="Chapter 05"
          kicker="Ecosystem Architecture"
          title="More Than an Investment Platform."
          subtitle="We are building a complete digital financial ecosystem around AI, digital assets, blockchain and financial technology."
        />

        <Reveal delay={0.15}>
          <div className="mt-10 flex flex-wrap gap-2" data-testid="ecosystem-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`ecosystem-filter-${f.key}`}
                className={`rounded-full px-5 py-2.5 text-sm font-medium transition-all duration-300 ${
                  filter === f.key
                    ? "bg-white text-[#08080A]"
                    : "border border-white/10 text-[#99A1AF] hover:border-white/30 hover:text-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {visible.map((item) => {
              const s = STATUS[item.status];
              return (
                <motion.div
                  layout
                  key={item.name}
                  initial={{ opacity: 0, scale: 0.94 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.94 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="group glass-card p-7 transition-colors duration-500 hover:border-[#D4AF37]/30"
                  data-testid={`ecosystem-card-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3 text-white transition-colors duration-500 group-hover:border-[#D4AF37]/30 group-hover:text-[#D4AF37]">
                      <item.icon className="h-5 w-5" />
                    </div>
                    <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-mono text-[10px] tracking-widest uppercase ${s.pill}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
                      {s.label}
                    </span>
                  </div>
                  <h3 className="mt-6 font-display text-lg font-semibold text-white">{item.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#99A1AF]">{item.desc}</p>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
